import { useEffect, useMemo, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronLeft, IconChevronRight, IconShare, IconX } from '@tabler/icons-react';
import { buildFloorPlanSrc } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import { Carousel } from 'components/marketplace/shared/Carousel';
import { CarouselDots } from 'components/marketplace/shared/CarouselDots';

interface PhotoCarouselProps {
  photos: GalleryPhoto[] | null | undefined;
  onClose: () => void;
  onOpenShareModal: () => void;
  isRounded?: boolean;
  onPhotoClick?: () => void;
}

type PresetTier = {
  minWidth: number;
  maxWidth: number | null;
  preset: string;
  presetScale: string;
};

// Mirrors the standalone PDP desktop gallery (GalleryPhotos
// GALLERY_SOURCE_SET_CONFIG) so the mini PDP carousel renders at parity on
// desktop. The <1201 tier matches the standalone PDP mobile carousel
// (components/marketplace/property/media/PhotoCarousel.tsx).
const PRESET_TIERS: PresetTier[] = [
  { minWidth: 0, maxWidth: 1201, preset: '3:2-md', presetScale: '2/3' },
  { minWidth: 1201, maxWidth: 1400, preset: '3:2-lg', presetScale: '3/4' },
  { minWidth: 1400, maxWidth: null, preset: '3:2-xl', presetScale: '1' },
];

// Below this viewport width the standalone PDP carousel pins dpr=1 to keep
// cellular payloads bounded; above it we follow the standalone PDP gallery's
// [1, 2, 3] srcset ladder by selecting the actual devicePixelRatio.
const MOBILE_DPR_CAP_MAX_WIDTH_PX = 1201;

const pickPresetTier = (viewportWidth: number): PresetTier =>
  PRESET_TIERS.find(
    (tier) =>
      viewportWidth >= tier.minWidth && (tier.maxWidth == null || viewportWidth < tier.maxWidth),
  ) ?? PRESET_TIERS[0];

const buildPhotoUrl = (
  photo: GalleryPhoto,
  viewportWidth: number,
  devicePixelRatio: number,
): string => {
  // Floor plans: skip the 3:2 crop preset because cabinet renders a cropped
  // source at those presets, which clips edges off portrait floor plans.
  if (photo.isFloorPlan) {
    return buildFloorPlanSrc(photo.url);
  }
  const tier = pickPresetTier(viewportWidth);
  const dpr =
    viewportWidth < MOBILE_DPR_CAP_MAX_WIDTH_PX
      ? 1
      : Math.max(1, Math.min(3, Math.round(devicePixelRatio)));
  const params = new URLSearchParams({
    preset: tier.preset,
    presetScale: tier.presetScale,
    service: 'cosmos',
    auto: 'format,compress',
    dpr: String(dpr),
  });
  return `${photo.url}?${params.toString()}`;
};

export const PhotoCarousel = ({
  photos,
  onClose,
  isRounded = false,
  onOpenShareModal,
  onPhotoClick,
}: PhotoCarouselProps) => {
  const [index, setIndex] = useState(0);
  // SSR default renders the mobile-equivalent URL (dpr=1, 3:2-md@2/3). The real
  // viewport/dpr are read on mount and the carousel upgrades to the standalone
  // PDP gallery preset on desktop / Retina. The aspect ratio is now driven by
  // CSS breakpoints (square on mobile, 16:9 on desktop) rather than JS.
  const [viewport, setViewport] = useState<{ width: number; dpr: number }>({
    width: 1024,
    dpr: 1,
  });

  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        width: window.innerWidth,
        dpr: window.devicePixelRatio || 1,
      });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  const mappedPhotos = useMemo(
    () =>
      photos?.map((photo) => ({
        url: buildPhotoUrl(photo, viewport.width, viewport.dpr),
        alt: photo.isFloorPlan ? photo.label?.trim() || 'Floor plan' : '',
      })) || [],
    [photos, viewport.width, viewport.dpr],
  );

  const count = mappedPhotos.length;

  return (
    <Carousel
      count={count}
      index={index}
      onIndexChange={setIndex}
      ariaLabel="Property photos"
      // shrink-0: the carousel sits at the top of a fixed-height, scrollable
      // flex column (the mini-PDP panel). Without it, the tall content below
      // shrinks the aspect-ratio carousel to zero height (CSS flex items default
      // to shrink:1, unlike the React Native default of 0 in the old bricks UI).
      className={cn('aspect-video w-full shrink-0', isRounded && 'rounded-t-4xl')}
      renderSlide={(i) => (
        <Image
          src={mappedPhotos[i]?.url}
          alt={mappedPhotos[i]?.alt || `Property photo ${i + 1}`}
          className="h-full w-full object-cover"
        />
      )}
    >
      <Button
        variant="secondary"
        aria-label="Close property details"
        analyticsName="marketplace-carousel-back-button"
        onClick={onClose}
        className="absolute left-6 top-6 z-[1] size-9 rounded-full border-od-border-tertiary p-0"
      >
        <IconX size={24} />
      </Button>
      <Button
        variant="secondary"
        aria-label="share"
        analyticsName="marketplace-carousel-share-button"
        onClick={onOpenShareModal}
        className="absolute right-6 top-6 z-[1] size-9 rounded-full border-od-border-tertiary p-0"
      >
        <IconShare size={24} />
      </Button>
      {index > 0 && (
        <Button
          variant="secondary"
          aria-label="Previous photo"
          analyticsName="marketplace-carousel-prev-button"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          // Arrows only from the `sm` breakpoint (640px) up — the nearest named
          // breakpoint to the old carousel's 600px cutoff; on narrow phones
          // they'd overlap the photo.
          className="absolute left-6 top-1/2 z-[1] hidden size-7 -translate-y-1/2 rounded-full border-od-border-tertiary p-0 sm:flex"
        >
          <IconChevronLeft size={20} />
        </Button>
      )}
      {index < count - 1 && (
        <Button
          variant="secondary"
          aria-label="Next photo"
          analyticsName="marketplace-carousel-next-button"
          onClick={() => setIndex((i) => Math.min(count - 1, i + 1))}
          className="absolute right-6 top-1/2 z-[1] hidden size-7 -translate-y-1/2 rounded-full border-od-border-tertiary p-0 sm:flex"
        >
          <IconChevronRight size={20} />
        </Button>
      )}
      <Box
        className="pointer-events-none absolute bottom-5 left-0 right-0 z-[1] items-center justify-center"
        aria-hidden="true"
      >
        <CarouselDots length={count} currentIndex={index} />
      </Box>
      {onPhotoClick && (
        // Transparent full-bleed tap target. No z-index, so the z-[1] controls
        // above stay clickable; the Carousel suppresses the click that follows
        // a swipe so this only fires on a genuine tap.
        <Box
          aria-label="View property details"
          onClick={onPhotoClick}
          analyticsName="marketplace-carousel-photo-clicked"
          className="absolute inset-0 h-full w-full bg-transparent p-0"
        />
      )}
    </Carousel>
  );
};
